import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { MathUtils } from "three";
import {
  newLayoutMinHeight,
  newLayoutMaxHeight,
} from "entities/configurator/model/layoutMeta";
import {
  defaultDoorHeight,
  defaultDoorWidth,
} from "shared/utils/doorSceneConstants";

const { clamp, lerp, mapLinear } = MathUtils;

export const bottomBeamOverlapScale = 1.07;
const cutLayoutOverflowMaxHeight = 3000;
const cutLayoutOverflowScaleBoost = 0.104;
const cutLayoutOverflowYOffset = -0.104;

const layoutAdjustments = [
  {
    match: (code: string) => code.includes("debeursdewaal"),
    outMin: 0.94,
    outMax: 0.968,
  },
  {
    match: (code: string) => code.includes("decooldewaal"),
    outMin: 0.987,
    outMax: 1.007,
  },
];
/** Configurable Y-scale rules based on layoutCode */
function getLayoutScaleY(layoutCode: string, height: number): number {
  const adjustmentRule = layoutAdjustments.find((l) => l.match(layoutCode));
  if (!adjustmentRule) return 1;

  return mapLinear(
    height,
    newLayoutMinHeight,
    newLayoutMaxHeight,
    adjustmentRule.outMin,
    adjustmentRule.outMax,
  );
}

export function useLayoutAdjustments({ doorIndex }: { doorIndex: number }) {
  const {
    getConfigurationWidth,
    getConfigurationHeight,
    layout,
    isCutLayout,
    selectedDoor,
  } = useConfigurationOptionsContext();

  const currentHeight = getConfigurationHeight(0);
  const currentWidth = getConfigurationWidth(doorIndex);
  const layoutCode = layout?.code ?? "";
  const layoutScaleY = getLayoutScaleY(layoutCode, currentHeight);

  let scaleDoorY = (currentHeight / defaultDoorHeight) * layoutScaleY;
  let scaleDoorYCut = scaleDoorY;
  let doorCutYAdjustment = 0;

  if (isCutLayout && currentHeight < 2541) {
    // Height ∈ [2315..2540] → scaleDoorYCut ∈ [0.81..0.95], doorCutYAdjustment ∈ [0.07..0.03]
    const tHeight = mapLinear(currentHeight, 2315, 2540, 0, 1);
    scaleDoorYCut = lerp(0.81, 0.95, tHeight);
    doorCutYAdjustment = lerp(0.07, 0.03, tHeight);
  } else if (isCutLayout && currentHeight > newLayoutMaxHeight) {
    const cappedOverflowHeight = clamp(
      currentHeight,
      newLayoutMaxHeight,
      cutLayoutOverflowMaxHeight,
    );
    const overflowProgress = mapLinear(
      cappedOverflowHeight,
      newLayoutMaxHeight,
      cutLayoutOverflowMaxHeight,
      0,
      1,
    );

    scaleDoorYCut =
      scaleDoorY + lerp(0, cutLayoutOverflowScaleBoost, overflowProgress);
    doorCutYAdjustment = lerp(0, cutLayoutOverflowYOffset, overflowProgress);
  }
  let scaleDoorX = currentWidth / defaultDoorWidth;

  let layoutOffsetX = 0;

  // Only apply extra scale factor for large widths: width ∈ [921..1750] → factor ∈ [1..1.2]
  if (currentWidth >= 921) {
    const factor = mapLinear(currentWidth, 921, 1750, 1, 1.2);
    scaleDoorX *= factor;
  }

  // ----- X adjustment -----
  // 0 until 1150; ramp 1150..1750 → 0.032..0.142; cap at 0.1615 ≥ 1750
  if (currentWidth > 1150 && currentWidth < 1750) {
    layoutOffsetX = mapLinear(currentWidth, 1150, 1750, 0.032, 0.142);
  } else if (currentWidth >= 1750) {
    layoutOffsetX = 0.1615;
  }

  return {
    scaleDoorX,
    scaleDoorY,
    scaleDoorYCut,
    layoutOffsetX,
    doorCutYAdjustment,
    isCutLayout,
    selectedDoor,
  };
}
